/**
 * **Die Kennzahlen-Karten** (HM-156) — `KennzahlenKarten` aus
 * `ui_kits/dashboard/KennzahlenKarten.jsx` (Variante 1a): drei Karten
 * Erzeugungsanlagen · Netzbezug · Verbrauch. Sie ersetzen die Kachelreihe aus
 * Netzbezug, Erzeugung, Einspeisung und Verbrauch.
 *
 * **Groß die Energie des Zeitraums, oben rechts die Leistung jetzt.** Darunter
 * je Teil eine Zeile: die Leistung klein, die Energie fett, beide in festen
 * rechtsbündigen Spalten. Was die Zahlen bedeuten und woher sie kommen, steht in
 * `lib/key-figures.ts`; hier wird nur gezeichnet.
 *
 * **Was fehlt, wird weggelassen** (Regel 8): ohne Momentanwerte keine
 * Leistungsspalte, ohne Wert eines Teils ein „–" an seiner Stelle und kein
 * Balken, der eine Null behauptete. Der Ladezustand steht nur, wenn ein Gerät
 * ihn **gerade** meldet — „jetzt" hängt nicht am Zeitraum.
 *
 * **Ein Akku mit eigenem Zähler steht unter einer Linie.** Die große Zahl ist
 * dann nur die PV; stünde er wie ein Teil darunter, rechnete jemand PV plus
 * Akku nach und erwartete eine andere Zahl.
 *
 * **Es stehen nur die Karten, die die Anlage führt** (HM-176, `figures.cards`):
 * an einem Haus ohne Erzeugung „Netzbezug" und „Verbrauch", an einer reinen
 * Erzeugung „Erzeugungsanlagen" und „Netzbezug". Das Raster folgt der Zahl der
 * Karten; die Verhältnisse der drei Spalten gelten nur, wo es drei sind.
 *
 * **Die Bausteine sind exportiert**, weil die Wohnung dieselbe Karte „Verbrauch"
 * zeigt — mit PV-Anteil und Netzanteil als Teilen (`routes/unit-electricity.tsx`).
 */
import { Children, type ReactNode } from "react";
import { quantityColorVar, useNarrow } from "@hausmeter/ui";
import { ENERGY_UNIT, formatEnergySummary, formatPercent, formatPowerFull } from "../lib/energy-format.js";
import { gridFlowNow, type KeyFigures } from "../lib/key-figures.js";
import { BATTERY_IDLE_W } from "../lib/generation-split.js";
import { tariffColor } from "../lib/grid-tariffs.js";

const MONO = "var(--font-mono)";
const BATTERY = "var(--hm-series-datapoint)";

const kwh = (wh: number | null): string => (wh === null ? "–" : formatEnergySummary(wh));
const kw = (w: number): string => formatPowerFull(w);
const pct = (ratio: number): number => Math.round(ratio * 100);

export function KeyFigureCards({
  figures,
  stateOfCharge,
  notes,
}: {
  figures: KeyFigures;
  /** Ladezustand jetzt, 0…1 — `null`, wenn keine Batterie ihn gerade meldet. */
  stateOfCharge: number | null;
  /** Ersatzwerte in einer Summe (`qualityNote`) — je Karte, sonst nichts. */
  notes?: { readonly supply?: string; readonly grid?: string; readonly consumption?: string };
}) {
  const narrow = useNarrow(1040);
  const { supply, grid, consumption } = figures;
  const battery = supply.battery;
  const supplyLive = supply.nowW !== null || supply.pvNowW !== null;
  const gridLive = grid.importNowW !== null || grid.exportNowW !== null;
  const gridFlow = gridFlowNow(grid.importNowW, grid.hasExport ? grid.exportNowW : null);
  const consumptionLive = consumption.nowW !== null || consumption.selfUseNowW !== null;
  const own = consumption.selfShare;
  const { cards } = figures;
  const count = [cards.supply, cards.grid, cards.consumption].filter(Boolean).length;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: narrow
          ? "minmax(0, 1fr)"
          : count === 3
            ? "minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 1.1fr)"
            : `repeat(${count}, minmax(0, 1fr))`,
        gap: 16,
      }}
    >
      {cards.supply && (
        <KeyFigureCard
          label="Erzeugungsanlagen"
          nowW={supply.nowW}
          totalWh={supply.totalWh}
          color={quantityColorVar("generation")}
          note={notes?.supply}
        >
          <KeyFigureRow
            live={supplyLive}
            color={quantityColorVar("generation")}
            label={battery?.inMeter === true ? "PV (brutto)" : "PV"}
            nowW={supply.pvNowW}
            wh={supply.pvWh}
          />
          {battery !== null && !battery.inMeter && (
            <div style={{ height: 1, background: "var(--border-subtle)" }} />
          )}
          {battery !== null && (
            <KeyFigureRow
              live={supplyLive}
              color={BATTERY}
              label="Akku"
              nowW={battery.nowW}
              signedNow
              /* Wie die Leistung: minus heißt heraus, plus heißt hinein. */
              energy={[
                {
                  text: battery.dischargedWh === null ? "–" : `−${kwh(battery.dischargedWh)}`,
                  title: "entladen",
                },
                {
                  text: battery.chargedWh === null ? "–" : `+${kwh(battery.chargedWh)}`,
                  title: "geladen",
                },
              ]}
            />
          )}
          {battery !== null && stateOfCharge !== null && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  flex: 1,
                  height: 8,
                  borderRadius: 4,
                  background: "var(--surface-sunken)",
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    display: "block",
                    height: "100%",
                    width: `${pct(stateOfCharge)}%`,
                    background: BATTERY,
                    borderRadius: 4,
                  }}
                />
              </div>
              <span style={{ font: `600 13px ${MONO}`, color: BATTERY }}>
                {formatPercent(stateOfCharge)}
              </span>
              <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>Ladezustand</span>
            </div>
          )}
        </KeyFigureCard>
      )}

      {/* Ohne Bezugszählwerk (eine reine Einspeisung) stünde groß „– kWh" unter
          „Netzbezug" — die Karte heißt dann nach dem, was gezählt wird. */}
      {cards.grid && !grid.hasImport && (
        <KeyFigureCard
          label="Einspeisung"
          nowW={grid.exportNowW}
          totalWh={grid.exportWh}
          color={quantityColorVar("grid_export")}
          note={notes?.grid}
        />
      )}

      {cards.grid && grid.hasImport && (
        <KeyFigureCard
          label="Netzbezug"
          /* Oben steht, was **jetzt** fließt (UI-Durchsicht U3): Beim Einspeisen
             zeigte die Karte „0 W" und verschwieg die 2 kW, die hinausgingen. */
          nowW={gridFlow.watts}
          nowLabel={gridFlow.word}
          totalWh={grid.importWh}
          color={quantityColorVar("grid_import")}
          note={notes?.grid}
        >
          <KeyFigureRow
            live={gridLive}
            color={quantityColorVar("grid_import")}
            label="Netzbezug"
            nowW={grid.importNowW}
            wh={grid.importWh}
          />
          {/* HT und NT (HM-167): Teile des Bezugs, eingerückt darunter — ohne Leistung,
            denn welcher Tarif gerade gilt, meldet der Zähler nicht. */}
          {grid.tariffs.map((tariff) => (
            <div key={tariff.label} style={{ paddingLeft: 15 }}>
              <KeyFigureRow
                live={gridLive}
                color={tariffColor(tariff.fade)}
                label={tariff.label}
                nowW={null}
                wh={tariff.wh}
              />
            </div>
          ))}
          {grid.hasExport && (
            <KeyFigureRow
              live={gridLive}
              color={quantityColorVar("grid_export")}
              label="Einspeisung"
              nowW={grid.exportNowW}
              wh={grid.exportWh}
            />
          )}
        </KeyFigureCard>
      )}

      {cards.consumption && (
        <KeyFigureCard
          label="Verbrauch"
          nowW={consumption.nowW}
          extra={
            own === null ? undefined : (
              <>
                Autarkie{" "}
                <b style={{ fontFamily: MONO, fontWeight: 500, color: "var(--text-muted)" }}>
                  {formatPercent(own)}
                </b>
              </>
            )
          }
          totalWh={consumption.totalWh}
          color={quantityColorVar("consumption")}
          note={notes?.consumption}
        >
          {consumption.split && (
            <>
              <ShareBar
                parts={
                  own === null
                    ? null
                    : [
                        { share: own, color: quantityColorVar("self_consumption") },
                        { share: 1 - own, color: quantityColorVar("grid_import") },
                      ]
                }
              />
              <KeyFigureRow
                live={consumptionLive}
                color={quantityColorVar("self_consumption")}
                label="Eigenverbrauch"
                share={own}
                nowW={consumption.selfUseNowW}
                wh={consumption.selfUseWh}
              />
              <KeyFigureRow
                live={consumptionLive}
                color={quantityColorVar("grid_import")}
                label="Netz"
                share={own === null ? null : 1 - own}
                nowW={grid.importNowW}
                wh={consumption.gridImportWh}
              />
            </>
          )}
        </KeyFigureCard>
      )}
    </div>
  );
}

/** Ob etwas zu zeichnen ist — `false` und `null` aus `cond && …` zählen nicht. */
function hasParts(children: ReactNode): boolean {
  return Children.toArray(children).length > 0;
}

/** Die Hülle einer Karte: Beschriftung, Leistung jetzt, große Zahl, Trennlinie, Teile. */
export function KeyFigureCard({
  label,
  nowW,
  nowLabel,
  extra,
  totalWh,
  color,
  note,
  children,
}: {
  label: string;
  nowW: number | null;
  /** Ein Wort vor der Leistung, wo sie in zwei Richtungen fließen kann („Einspeisung"). */
  nowLabel?: string | undefined;
  /** Unter der Leistung, rechtsbündig — beim Verbrauch der Autarkiegrad. */
  extra?: ReactNode;
  totalWh: number | null;
  color: string;
  note?: string | undefined;
  /** Die Teile — ohne sie (Verbrauch ohne Erzeugung) entfällt auch die Linie darüber. */
  children?: ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-muted)" }}>{label}</span>
        <span style={{ position: "relative" }}>
          {nowW !== null && (
            <span
              title="Leistung jetzt"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                font: `600 14px ${MONO}`,
                fontVariantNumeric: "tabular-nums",
                color: "var(--text-strong)",
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--hm-green-500)",
                }}
              />
              {nowLabel !== undefined && (
                <span style={{ font: "500 12.5px var(--font-body)", color: "var(--text-muted)" }}>
                  {nowLabel}
                </span>
              )}
              {kw(nowW)}
            </span>
          )}
          {extra !== undefined && (
            <span
              style={{
                position: nowW === null ? "static" : "absolute",
                right: 0,
                top: "100%",
                marginTop: 3,
                whiteSpace: "nowrap",
                fontSize: 12.5,
                color: "var(--text-faint)",
              }}
            >
              {extra}
            </span>
          )}
        </span>
      </div>
      <div>
        <span
          style={{
            font: `600 26px/1.1 ${MONO}`,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-.02em",
            color: totalWh === null ? "var(--text-faint)" : color,
          }}
        >
          {kwh(totalWh)}
        </span>
        <span style={{ font: `500 14px ${MONO}`, color: "var(--text-muted)", marginLeft: 4 }}>
          {ENERGY_UNIT}
        </span>
        {note !== undefined && (
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-faint)" }}>{note}</div>
        )}
      </div>
      {hasParts(children) && (
        <>
          <div style={{ height: 1, background: "var(--border-subtle)" }} />
          {children}
        </>
      )}
    </section>
  );
}

/**
 * Eine Zeile: Farbmarke, Name, Anteil — rechts die Leistung klein, die Energie
 * fett. `live` sagt, ob die Karte eine Leistungsspalte hat; eine Zeile ohne
 * eigenen Momentanwert lässt ihre Zelle dann leer, statt die Spalte zu verschieben.
 */
export function KeyFigureRow({
  live,
  color,
  label,
  share,
  nowW,
  signedNow = false,
  wh,
  energy,
}: {
  live: boolean;
  color: string;
  label: string;
  /** Anteil 0…1 neben dem Namen — `null` oder fehlend: keiner. */
  share?: number | null;
  nowW: number | null;
  /** Leistung mit Vorzeichen zeigen (Akku: plus lädt, minus entlädt). */
  signedNow?: boolean;
  wh?: number | null;
  /** Mehrere Energiewerte untereinander statt `wh` — beim Akku entladen und geladen. */
  energy?: readonly { readonly text: string; readonly title: string }[];
}) {
  const now =
    nowW === null
      ? ""
      : signedNow
        ? `${Math.abs(nowW) < BATTERY_IDLE_W ? "" : nowW > 0 ? "+" : "−"}${kw(Math.abs(nowW))}`
        : kw(nowW);
  const lines = energy ?? [{ text: kwh(wh ?? null), title: label }];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        fontSize: 13,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          whiteSpace: "nowrap",
          minWidth: 0,
          color: "var(--text-body)",
          lineHeight: "18px",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
        {label}
        {share !== undefined && share !== null && (
          <span style={{ font: `12px ${MONO}`, color: "var(--text-faint)" }}>
            {formatPercent(share)}
          </span>
        )}
      </span>
      <span
        style={{
          display: "grid",
          gridTemplateColumns: live
            ? "minmax(58px, auto) minmax(66px, auto)"
            : "minmax(66px, auto)",
          gap: 8,
          textAlign: "right",
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
          lineHeight: "18px",
        }}
      >
        {live && <span style={{ font: `12px ${MONO}`, color: "var(--text-faint)" }}>{now}</span>}
        <span
          style={{
            font: `600 14px ${MONO}`,
            color: "var(--text-strong)",
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.3,
          }}
        >
          {lines.map((line) => (
            <span key={line.title} title={line.title}>
              {line.text} {ENERGY_UNIT}
            </span>
          ))}
        </span>
      </span>
    </div>
  );
}

/** Der Anteilsbalken unter der Trennlinie — ohne Anteile keiner. */
export function ShareBar({
  parts,
}: {
  parts: readonly { readonly share: number; readonly color: string }[] | null;
}) {
  if (parts === null) return null;
  return (
    <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", gap: 2 }}>
      {parts
        .filter((part) => part.share > 0)
        .map((part, index) => (
          <span key={index} style={{ flex: `${part.share} 1 0`, background: part.color }} />
        ))}
    </div>
  );
}
